import { useCallback, useLayoutEffect, useState } from "react";

import type { RefCallback } from "react";

interface UseElementHeightResult {
  ref: RefCallback<HTMLDivElement>;
  height: number;
}

/**
 * Tracks an element's rendered height via ResizeObserver rather than a
 * hardcoded constant, so a sticky offset derived from it can never drift out
 * of sync with what actually renders (e.g. the toolbar gaining a control
 * behind a flag).
 *
 * Uses a callback ref (state, not a plain ref object) so the observer attaches
 * whenever the DOM node shows up — including when the caller's JSX mounts the
 * element on a later render (e.g. behind a loading gate) rather than on this
 * hook's own first commit.
 */
export const useElementHeight = (): UseElementHeightResult => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  const ref = useCallback<RefCallback<HTMLDivElement>>((node) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) return;

    const elementHeight = element.getBoundingClientRect().height;
    // Avoid crashing in environments where ResizeObserver isn't available.
    if (typeof ResizeObserver === "undefined") {
      setHeight(elementHeight);
      return;
    }
    // Set an initial value to avoid a 0px flash before the first observer callback.
    setHeight(elementHeight);

    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, height };
};
